import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { useImpersonationStore } from '@/shared/impersonation/impersonation.store';
import { SYSTEM_ADMINISTRATOR } from '@/shared/guards/roles';
import { useViewAsStore } from './view-as.store';

/**
 * Papéis efetivos para o gating de UI. Quando o sys-admin está "visualizando
 * como" um papel, esse papel substitui os reais (sem o curinga
 * SYSTEM_ADMINISTRATOR), para o gating se comportar como o do papel testado.
 * Sob "atuar como", os do alvo — e nenhum enquanto carregam ou se falharam: o
 * menu do ator na tela do aluno não reproduz o que o aluno vê. Senão, os
 * papéis reais.
 */
export function useEffectiveRoles(): string[] {
  const auth = useAuthStore((s) => s.auth);
  const viewAsRole = useViewAsStore((s) => s.viewAsRole);
  const target = useImpersonationStore((s) => s.target);
  const targetRoles = useImpersonationStore((s) => s.targetRoles);
  if (viewAsRole) return [viewAsRole];
  if (target) return targetRoles.status === 'success' ? targetRoles.roles : [];
  return auth?.roles ?? [];
}

/**
 * Estado das roles do alvo, para a tela distinguir "carregando" e "falhou" de
 * "o aluno não tem role". `null` quando elas não decidem nada: sem máscara, ou
 * com o "visualizar como", que tem precedência.
 */
export function useTargetRolesStatus(): 'pending' | 'error' | 'success' | null {
  const viewAsRole = useViewAsStore((s) => s.viewAsRole);
  const target = useImpersonationStore((s) => s.target);
  const status = useImpersonationStore((s) => s.targetRoles.status);
  return viewAsRole || !target ? null : status;
}

/**
 * Apenas um SYSTEM_ADMINISTRATOR **real** (ignora o override) pode ativar o
 * "visualizar como". Evita que o próprio modo de teste seja usado para
 * escalar acesso.
 */
export function useIsRealAdmin(): boolean {
  const auth = useAuthStore((s) => s.auth);
  return (auth?.roles ?? []).includes(SYSTEM_ADMINISTRATOR);
}
